import { wccModules } from 'enums/wccModules';
import { inject, injectable } from 'inversify';
import { PureComputed } from 'knockout';
import { CurrentUserManager } from 'managers/currentUser';
import IdeationManager from 'managers/ideation';
import { IWCCStorageManager } from 'managers/iStorage';
import TaskManager from 'managers/task';
import { TopicManager } from 'managers/topic';
import { Person } from 'models/person';
import Task from 'models/task';
import { Topic } from 'models/topic';

const { discussionId } = settings;

export interface TopicContentPlaceholdersManagerConfig {
    topicId: string
}

@injectable()
export default class TopicContentPlaceholdersManager {
    titlePlaceholder: PureComputed<string>
    commentPlaceholder: PureComputed<string>
    replyPlaceholder: PureComputed<string>

    ready: PureComputed<boolean>

    constructor(
        @inject(wccModules.managerConfig) { topicId }: TopicContentPlaceholdersManagerConfig,
        @inject(wccModules.storage) storage: IWCCStorageManager
    ) {
        const userManager = storage.get(CurrentUserManager, { discussionId });
        const user = userManager.pluck(m => m.person);

        const taskManager = storage.get(TaskManager, { taskId: discussionId });
        const task = taskManager.pluck(m => m.task);

        const topicManager = storage.get(TopicManager, { topicId });
        const topic = topicManager.pluck(m => m.topic);

        const ideationManager = storage.get(IdeationManager, { topicId });

        this.titlePlaceholder = ko.strictComputed((user, topic) => {
            if (user != undefined && topic != undefined)
                return this.getTitlePlaceholder(user, topic) ?? '';

            return '';
        }, [user, topic]);

        this.commentPlaceholder = ko.strictComputed((user, topic) => {
            if (user != undefined && topic != undefined)
                return this.getCommentPlaceholder(user, topic);

            return '';
        }, [user, topic]);

        this.replyPlaceholder = ko.strictComputed((user, task, topic, ideationManager) => {
            if (user != undefined && task != undefined && topic != undefined && ideationManager != undefined)
                return this.getReplyPlaceholder(user, task, topic, ideationManager);

            return '';
        }, [user, task, topic, ideationManager]);

        this.ready = ko.pureComputed(() => user() != undefined && task() != undefined && topic() != undefined);
    }

    private getTitlePlaceholder(user: Person, topic: Topic) {
        let result = topic.titlePlaceholder();

        if (result == undefined && topic.isIdeation() && user.isRegular())
            return labels.GiveYourIdeaATitle;

        return result;
    }

    private getCommentPlaceholder(user: Person, topic: Topic) {
        let result = topic.commentPlaceholder();

        if (result == undefined || user.isModerator() || user.isObserver()) {
            if (topic.isIdeation() && user.isRegular())
                return labels.WhatIsYourIdea;
            else
                return labels.WhatWouldYouLikeToSay;
        }

        return result;
    }

    private getReplyPlaceholder(user: Person, task: Task, topic: Topic, ideation: IdeationManager) {
        let result = topic.replyPlaceholder();

        if (result == undefined || user.isModerator() || user.isObserver()) {
            if (topic.isIdeation()) {
                if (ideation.isVotingStage() && !user.isModerator() && !user.isObserver())
                    return labels.WhyDidYouVoteThisWay;
                else
                    return labels.CommentOnThisPost;
            } else if (task.isHomework()) {
                return labels.AnythingElseYouWouldLikeToAdd;
            } else {
                return labels.CommentOnThisPost;
            }
        }

        return result;
    }
}